import React from 'react';
import styled from 'styled-components';

const AboutContainer = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #2c5aa0 0%, #1e3a8a 100%);
  color: #fff;
  padding: 80px 0;
  text-align: center;

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 15px;
  }
`;

const Title = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Subtitle = styled.p`
  font-size: 20px;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ContentSection = styled.section`
  padding: 80px 0;
  background: #fff;

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const ContentGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
    padding: 0 15px;
  }
`;

const TextContent = styled.div`
  h2 {
    font-size: 36px;
    color: #2c5aa0;
    margin-bottom: 30px;

    @media (max-width: 768px) {
      font-size: 28px;
    }
  }

  p {
    font-size: 18px;
    line-height: 1.8;
    color: #666;
    margin-bottom: 20px;

    @media (max-width: 768px) {
      font-size: 16px;
    }
  }
`;

const ImagePlaceholder = styled.div`
  background: #f8f9fa;
  height: 400px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 18px;
  border: 2px dashed #dee2e6;

  @media (max-width: 768px) {
    height: 300px;
  }
`;

const ValuesSection = styled.section`
  padding: 80px 0;
  background: #f8f9fa;

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const ValuesGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
    padding: 0 15px;
  }
`;

const ValueCard = styled.div`
  background: #fff;
  padding: 40px 30px;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }

  svg {
    font-size: 50px;
    color: #2c5aa0;
    margin-bottom: 20px;
  }

  h3 {
    font-size: 24px;
    color: #333;
    margin-bottom: 15px;
  }

  p {
    color: #666;
    line-height: 1.6;
  }
`;

const About: React.FC = () => {
  return (
    <AboutContainer>
      <HeroSection>
        <HeroContent>
          <Title>환영합니다</Title>
          <Subtitle>얼바인 중앙침례교회에 오신 여러분을 진심으로 환영합니다</Subtitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <ContentGrid>
          <TextContent>
            <h2>얼바인 중앙침례교회</h2>
            <p>
              저희 교회는 <strong>한국의 강남중앙침례교회의 지교회</strong>이며, 
              성경 말씀을 중심으로 하나님의 뜻을 따라가는 교회입니다.
            </p>
            <p>
              우리는 하나님의 말씀을 통해 지역사회에 복을 전하고, 
              모든 사람이 하나님의 사랑을 경험할 수 있도록 섬기는 교회입니다.
              새로운 가족들을 환영하며, 함께 성장해 나가는 교회가 되고자 합니다.
            </p>
          </TextContent>
          <ImagePlaceholder>
            교회 건물 이미지
          </ImagePlaceholder>
        </ContentGrid>
      </ContentSection>

      <ValuesSection>
        <ContentGrid>
          <TextContent>
            <h2>우리의 가치</h2>
            <p>
              우리는 하나님의 말씀을 중심으로 한 삶, 기도하는 공동체, 
              서로를 사랑하고 섬기는 마음, 그리고 지역사회에 복을 전하는 
              사명을 가지고 있습니다.
            </p>
          </TextContent>
          <ValuesGrid>
            <ValueCard>
              <span style={{ fontSize: '50px', color: '#2c5aa0' }}>👥</span>
              <h3>공동체</h3>
              <p>서로를 사랑하고 섬기는 공동체를 만들어갑니다</p>
            </ValueCard>
            <ValueCard>
              <span style={{ fontSize: '50px', color: '#2c5aa0' }}>❤️</span>
              <h3>사랑</h3>
              <p>하나님의 사랑을 실천하는 교회입니다</p>
            </ValueCard>
            <ValueCard>
              <span style={{ fontSize: '50px', color: '#2c5aa0' }}>🤝</span>
              <h3>섬김</h3>
              <p>지역사회에 복을 전하며 섬기는 교회입니다</p>
            </ValueCard>
            <ValueCard>
              <span style={{ fontSize: '50px', color: '#2c5aa0' }}>🙏</span>
              <h3>기도</h3>
              <p>기도하는 공동체로 성장해 나갑니다</p>
            </ValueCard>
          </ValuesGrid>
        </ContentGrid>
      </ValuesSection>
    </AboutContainer>
  );
};

export default About; 